import {
  createAsyncThunk,
  createSelector,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import type { WritableDraft } from 'immer/dist/types/types-external';
import {
  deleteAppNotification,
  getAppNotifications,
  putAppNotificationRead,
  putAppNotificationReadAll,
} from 'shared/api';
import { APP_NOTIFICATION_CONFIG, AppNotification, Page } from 'types';
import { AppState } from './reducers';

const PAGE_SIZE = 50;

// STATE
export const getInitialState = () => ({
  // DATA
  hasLoaded: false,
  isLoading: false,
  hasMore: false,

  appNotifications: {} as Record<AppNotification['id'], AppNotification>,
  earliestDateCreated: undefined as undefined | string,

  getListError: '',
  putReadError: '',

  // UI
  uiWantsModalOpen: false,
});
const initialState = getInitialState();
export type AppNotificationState = typeof initialState;

export const fetchAppNotifications = createAsyncThunk<
  Page<AppNotification>,
  void,
  { state: AppState }
>('app-notification/fetchAppNotifications', async (_, { getState }) => {
  const { earliestDateCreated } = getState().appNotification;
  const res = await getAppNotifications({
    page: 1,
    pageSize: PAGE_SIZE,
    dateCreated: earliestDateCreated,
  });
  return res.data;
});

export const removeAppNotification = createAsyncThunk<
  void,
  AppNotification['id'],
  { state: AppState }
>('app-notification/removeAppNotification', async appNotificationId => {
  await deleteAppNotification({ id: appNotificationId });
});

export const readAppNotification = createAsyncThunk<
  void,
  AppNotification['id'] | void,
  { state: AppState }
>('app-notification/readAppNotification', async appNotificationId => {
  if (appNotificationId) {
    await putAppNotificationRead(appNotificationId);
  } else {
    await putAppNotificationReadAll();
  }
});

// Reducer helper
const updateEarliestDateCreated = (
  state: WritableDraft<AppNotificationState>,
  an: AppNotification,
) => {
  if (
    !state.earliestDateCreated ||
    Date.parse(state.earliestDateCreated) > Date.parse(an.dateCreated)
  ) {
    state.earliestDateCreated = an.dateCreated;
  }
};

// Popup filter
export const isAppNotificationPopupFresh = (an: AppNotification) =>
  !an.dateRead && !!an.popupExpiration && Date.now() < an.popupExpiration;

// Sound playable filter
export const isAppNotificationSoundPlayEligible = (an: AppNotification) => !an.dateRead;

// Decorate FE-only data
const decorateAppNotification = (an: AppNotification) => {
  const puto = APP_NOTIFICATION_CONFIG[an.notificationType].popupTimeout;
  if (puto) {
    an.popupExpiration = Date.parse(an.dateCreated) + puto;
    an.popupDuration = puto;
    an.isPopupExpired = !isAppNotificationPopupFresh(an);
  }
  return an;
};

// SLICE
const appNotificationSlice = createSlice({
  name: 'app-notification',
  initialState: getInitialState(),
  reducers: {
    resetAppNotificationState() {
      return getInitialState();
    },
    receivedAppNotification: (state, action: PayloadAction<AppNotification>) => {
      state.appNotifications[action.payload.id] = decorateAppNotification(action.payload);
      updateEarliestDateCreated(state, action.payload);
    },
    appNotificationExpired: (state, action: PayloadAction<AppNotification['id']>) => {
      if (state.appNotifications[action.payload]) {
        state.appNotifications[action.payload].isPopupExpired = true;
      }
    },
    uiWantsModalOpenChanged: (state, action: PayloadAction<boolean>) => {
      state.uiWantsModalOpen = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchAppNotifications.pending, (state, _) => {
        state.isLoading = true;
      })
      .addCase(fetchAppNotifications.fulfilled, (state, action) => {
        state.isLoading = false;
        action.payload.items.forEach(an => {
          // set in the map
          state.appNotifications[an.id] = decorateAppNotification(an);
          // set the latest date to use as cursor for fetching more
          updateEarliestDateCreated(state, an);
        });
        state.hasLoaded = true;
        state.hasMore = action.payload.items.length == PAGE_SIZE;
      })
      .addCase(fetchAppNotifications.rejected, (state, payload) => {
        state.isLoading = false;
        state.getListError = payload.error.message || 'Unable to get app notifications';
      });

    builder.addCase(readAppNotification.fulfilled, (state, action) => {
      const nowIsoStr = new Date().toISOString();
      if (action.meta.arg && action.meta.arg in state.appNotifications) {
        // single
        state.appNotifications[action.meta.arg].dateRead = nowIsoStr;
      } else {
        // all
        Object.values(state.appNotifications).forEach(an => (an.dateRead = nowIsoStr));
      }
    });

    builder
      .addCase(removeAppNotification.pending, (state, action) => {
        if (state.appNotifications[action.meta.arg]) {
          state.appNotifications[action.meta.arg].isOptimisticDelete = true;
        }
      })
      .addCase(removeAppNotification.fulfilled, (state, action) => {
        if (state.appNotifications[action.meta.arg]) {
          delete state.appNotifications[action.meta.arg];
        }
      })
      .addCase(removeAppNotification.rejected, (state, action) => {
        if (state.appNotifications[action.meta.arg]) {
          state.appNotifications[action.meta.arg].isOptimisticDelete = false;
        }
      });
  },
});

export const selectAppNotificationsUnreadCount = createSelector(
  (s: AppState) => s.appNotification.appNotifications,
  ns => {
    const count = Object.values(ns).reduce((cnt, n) => (!n.dateRead ? cnt + 1 : cnt), 0);
    return count;
  },
);

export const selectAppNotificationsList = createSelector(
  (s: AppState) => s.appNotification.appNotifications,
  ns => {
    const list = Object.values(ns);
    list.sort((a, b) => Date.parse(b.dateCreated) - Date.parse(a.dateCreated));
    return list;
  },
);

export const selectAppNotificationsListPopupable = createSelector(
  (s: AppState) => selectAppNotificationsList(s),
  all => {
    const currentPopupables = all.filter(isAppNotificationPopupFresh);
    return currentPopupables;
  },
);

export const selectAppNotificationsListSoundPlayable = createSelector(
  (s: AppState) => selectAppNotificationsList(s),
  all => {
    const soundPlayables = all.filter(isAppNotificationSoundPlayEligible);
    return soundPlayables;
  },
);

// EXPORTS
export const {
  resetAppNotificationState,
  receivedAppNotification,
  uiWantsModalOpenChanged,
  appNotificationExpired,
} = appNotificationSlice.actions;

export default appNotificationSlice.reducer;
